<template>
  <div>
    <Page
      title="アセスメントサーベイグループ"
      helpInfoTitle="アセスメントサーベイグループでサーベイの目的と受検期間を設定し、グループの詳細から受検者を追加できます"
    >
      <template v-slot:action>
        <ActionButton
          v-if="!isTrial"
          title="月別受検数"
          color="primary"
          shape="round"
          width="160"
          @click="onMonthlyReportButtonClick"
          uk-tooltip="title: 前月分までの受検者数が確認できます<br />受検者数の確定は翌月の月初に行います; pos: left; cls: uk-active uk-width-auto;"
        />
      </template>
      <template v-slot:content>
        <div class="table-action">
          <div class="toggles">
            <label class="item-name">種別</label>
            <select class="uk-select uk-margin-small-left uk-width-small" v-model="type" @change="forceOnSearch">
              <option v-for="(type, i) in types" :key="i" :value="type.value">
                {{ type.label }}
              </option>
            </select>
            <div class="field">
              <FieldLabel title="期間中のみ表示" />
              <Toggle class="toggle-blue" v-model="onPeriod" on-label="ON" off-label="OFF" v-on:change="forceOnSearch" />
            </div>
            <div class="field">
              <FieldLabel title="受検完了グループの非表示" />
              <Toggle class="toggle-blue" v-model="excludeCompletion" on-label="ON" off-label="OFF" v-on:change="forceOnSearch" />
            </div>
          </div>

          <div class="right-contents">
            <div class="search-field uk-margin-small-left uk-margin-small-right">
              <SearchField
                @search="onSearch"
                :count="broadcasts && broadcasts.length"
                :tableData="broadcasts"
                :filterColumns="filterColumns"
                width="180px"
                :key="renewKey"
              />
            </div>
            <ActionButton title="新規追加" class="add-button" color="default" icon="plus" @click="onAddButtonClick" />
          </div>
        </div>
        <div class="uk-card uk-card-default uk-card-body">
          <Table :header="header" :tableData="broadcasts" @column-click="onColumnClick" />
        </div>
      </template>
    </Page>
    <div class="uk-flex-top monthly-report" uk-modal ref="monthly-report" :bg-close="false">
      <div class="uk-modal-dialog uk-modal-body uk-margin-auto-vertical form">
        <MonthlyReport />
      </div>
    </div>
    <div class="uk-flex-top editor" uk-modal ref="editor" :bg-close="false">
      <ASGroupForm :key="editorFormKey" @submitted="onGroupFormSubmitted" />
    </div>
  </div>
</template>
<style lang="scss" scoped>
@include table-action;
@include modal-form;
.action .action-button:first-child {
  margin-right: $spacing-medium;
}
.uk-modal-dialog {
  width: unset;
}

div.table-action {
  display: flex;
  justify-content: space-between;
}

div.toggles {
  display: inline-flex;
  align-items: center;

  > .item-name {
    min-width: 36px;
  }
}
div.toggles > div.field {
  margin-bottom: unset;
  margin-left: 32px;
}
div.toggle-container {
  margin-left: 8px;
}
.toggle-blue {
  --toggle-bg-on: #008080;
  --toggle-border-on: #008080;
}

.right-contents {
  display: flex;
  align-items: center;
  > .add-button {
    min-width: 150px;
  }
}
</style>
<style src="@vueform/toggle/themes/default.css"></style>
<script lang="ts">
import { Vue, Options } from "vue-class-component";
import { Ref } from "vue-property-decorator";
import Page from "@/components/molecules/Page.vue";
import ActionButton from "@/components/atoms/ActionButton.vue";
import Table from "@/components/molecules/Table.vue";
import MonthlyReport from "./MonthlyReport.vue";
import { AsApi, GetAsBroadcasts, AsBroadcastTypeEnum, PricePlanEnum } from "../../../autogen";
import SearchField from "../../atoms/SearchField.vue";
import moment from "moment-timezone";
import UIkit from "uikit";
import ASGroupForm from "./ASGroupForm.vue";
import Store from "../../../store";

import Toggle from "@vueform/toggle";
import FieldLabel from "../../atoms/FieldLabel.vue";

@Options({
  components: {
    Page,
    ActionButton,
    Table,
    MonthlyReport,
    SearchField,
    ASGroupForm,
    Toggle,
    FieldLabel
  }
})
export default class ASGroup extends Vue {
  @Ref("monthly-report")
  monthlyReport!: HTMLElement;
  @Ref("editor")
  editor!: HTMLElement;
  query = "";
  broadcasts: (GetAsBroadcasts & {
    typeName: string;
    term: string;
    count: string;
  })[] = [];
  broadCast?: GetAsBroadcasts;
  header = [
    { title: "種別", sortable: true, key: "typeName", width: 150 },
    { title: "実施内容名", sortable: true, key: "name", width: "expand" },
    { title: "受検期間", key: "term", width: "expand" },
    { title: "受検人数", key: "count", width: 150 },
    { title: "詳細", type: "button", key: "detail", width: 150 }
  ];
  filterColumns = ["name", "typeName", "term"];
  editorFormKey = 0;
  private readonly authStore = Store.getInstance();
  isTrial = true;
  onPeriod = true;
  excludeCompletion = false;
  type: AsBroadcastTypeEnum | "all" = "all";
  types = [
    { label: "全て", value: "all" },
    { label: "従業員", value: AsBroadcastTypeEnum.Employee },
    { label: "応募者", value: AsBroadcastTypeEnum.Applicant }
  ] as const;
  renewKey = 0;

  async loadData() {
    if (sessionStorage.getItem("asgConditions")) {
      const storedConditions = JSON.parse(sessionStorage.getItem("asgConditions")!);
      this.type = storedConditions.type;
      this.onPeriod = storedConditions.period;
      this.excludeCompletion = storedConditions.excludeCompletion;
    }
    const response = await new AsApi().getAsBroadcasts({
      q: this.query,
      onPeriod: this.onPeriod,
      excludeCompletion: this.excludeCompletion,
      type: this.type === "all" ? undefined : this.type
    });
    this.broadcasts = response.data.map(d =>
      Object.assign(
        {
          typeName: d.type === AsBroadcastTypeEnum.Employee ? "従業員" : "応募者",
          term: `${moment(d.startDateTime).format("YYYY月MM月DD日")}
            ~ ${moment(d.endDateTime).format("YYYY月MM月DD日")}`,
          count: `${d.participatedCount} / ${d.participantsCount}`
        },
        d
      )
    );
    const planCode = this.authStore.getAuth()!.pricePlan;
    this.isTrial = planCode === PricePlanEnum.Trial;
    this.renewKey = Date.now();
  }
  async beforeMount() {
    await this.loadData();
  }
  onColumnClick(location: { row: number; col: string }) {
    if (location.col === "detail") {
      const broadcast = this.broadcasts[location.row];
      this.$router.push(`/clients/${this.$route.params["clientId"]}/as/group/${broadcast.asBroadcastId}`);
    }
  }
  onMonthlyReportButtonClick() {
    UIkit.modal(this.monthlyReport).show();
  }
  async forceOnSearch() {
    sessionStorage.setItem(
      "asgConditions",
      JSON.stringify({ type: this.type, period: this.onPeriod, excludeCompletion: this.excludeCompletion })
    );
    await this.loadData();
  }
  async onSearch(searchResult: [] | null) {
    if (searchResult) {
      this.broadcasts = searchResult;
    } else {
      await this.loadData();
    }
  }
  onAddButtonClick() {
    this.editorFormKey = Date.now();
    UIkit.modal(this.editor).show();
  }
  async onGroupFormSubmitted() {
    UIkit.modal(this.editor).hide();
    await this.loadData();
  }
}
</script>
